/** Sidebar form for creating a patient note from eligibility details. */

import ArrowLeft from "@/svg/ArrowLeft";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Select, TextInput } from "risa-oasis-ui_v2";
import type {
  CreateNoteRequest,
  NoteAssigneeResponse,
} from "../../../api/clinicalTrialsApiTypes";
import {
  formatDisplayDate,
  parseIsoCalendarDate,
} from "../../../utils/displayDate";
import { plainTextToRichText } from "../../../utils/richText";
import { NoteTextAreaField } from "./NoteTextAreaField";

/** Props for the add-note sidebar. */
export interface AddNotePanelProps {
  assignees: NoteAssigneeResponse[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (request: CreateNoteRequest) => Promise<void>;
}

/** Format a local date as the notes API's YYYY-MM-DD calendar string. */
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Due-date field with Oasis styling and a calendar popover. */
function NoteDueDateField({
  value,
  onChange,
}: {
  value: string;
  onChange: (isoDate: string) => void;
}) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsCalendarOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isCalendarOpen]);

  return (
    <div className="relative flex flex-col gap-1.5" ref={containerRef}>
      <span className="text-x-tiny font-medium text-primaryGray-3">Due Date</span>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isCalendarOpen}
        aria-label="Due date"
        className="flex w-full items-center justify-between rounded-md border border-primaryGray-14 bg-white px-3 py-2 text-sm text-primaryGray-1 outline-none focus:ring-2 focus:ring-tertiaryBlue-4"
        onClick={() => setIsCalendarOpen((open) => !open)}
      >
        <span className={value ? "" : "text-primaryGray-6"}>
          {value ? formatDisplayDate(value, "MM/DD/YYYY") : "MM/DD/YYYY"}
        </span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M5 1v3M11 1v3M2 6h12M3 3h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {isCalendarOpen ? (
        <div
          role="dialog"
          className="absolute left-0 top-full z-50 mt-1 rounded-md border border-primaryGray-14 bg-white p-2 shadow-lg"
        >
          <DatePicker
            initialDate={value ? parseIsoCalendarDate(value) : undefined}
            onDateChange={(date) => {
              if (date) {
                onChange(toIsoDate(date));
                setIsCalendarOpen(false);
              }
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Render the full-height Add Note subview within the patient-notes sidebar. */
export function AddNotePanel({
  assignees,
  isSaving,
  onClose,
  onSave,
}: AddNotePanelProps) {
  const [assigneeId, setAssigneeId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [title, setTitle] = useState("");
  const [noteText, setNoteText] = useState("");

  const assigneeOptions = useMemo(
    () => [
      { value: "", label: "Select Assignee" },
      ...assignees.map((member) => ({
        value: member.user_id,
        label: member.display_name || member.email || "Unknown",
      })),
    ],
    [assignees],
  );
  const isValid = Boolean(title.trim() && noteText.trim());

  const handleSave = async () => {
    if (!isValid || isSaving) return;
    try {
      await onSave({
        title: title.trim(),
        body: plainTextToRichText(noteText.trim()),
        assignee_id: assigneeId.trim() || undefined,
        due_date: dueDate || undefined,
      });
      onClose();
    } catch {
      toast.error("Failed to add notes");
    }
  };

  return (
    <div className="add-note-panel flex h-full min-h-0 flex-col bg-white">
      <div className="flex h-[3.75rem] shrink-0 items-center gap-3 border-b border-primaryGray-14 px-4">
        <button
          type="button"
          aria-label="Back to patient notes"
          className="border-0 bg-transparent p-0 transition-transform hover:scale-105"
          onClick={onClose}
        >
          <ArrowLeft />
        </button>
        <h2 className="text-sm font-semibold text-primaryGray-1">Add Note</h2>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-4">
          <Select
            id="add-note-assignee"
            label="Assignee"
            placeholder="Select Assignee"
            options={assigneeOptions}
            value={assigneeId}
            onOptionChange={(option) => setAssigneeId(option.value)}
          />
          <NoteDueDateField value={dueDate} onChange={setDueDate} />
          <TextInput
            id="add-note-title"
            label="Title"
            placeholder="Enter Title"
            defaultValue={title}
            onChange={(data) => setTitle(data.value ?? "")}
          />
          <NoteTextAreaField
            id="add-note-body"
            label="Note"
            placeholder="Enter Note"
            value={noteText}
            onChange={setNoteText}
          />
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-primaryGray-14 p-3">
        <button
          type="button"
          className="rounded border border-primaryGray-2 bg-white px-4 py-2 text-sm font-semibold text-primaryGray-1"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded bg-primaryGray-1 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isSaving || !isValid}
          onClick={() => void handleSave()}
        >
          {isSaving ? "Adding…" : "Add"}
        </button>
      </div>
    </div>
  );
}
